import { forwardRef, memo, useState } from 'react';
import { useTheme } from 'styled-components/macro';
import { useTitleMarquee } from './hooks/use-title-marquee';
import { AlertProps } from './alert.types';
import { optionsMap } from './alert.constants';
import {
  AlertContainer,
  MeasureSpan,
  TitleText,
  TitleTrack,
  TitleWrapper,
} from './alert.styled';
import { Icons } from 'assets';
import { SVG } from '../../svg';
import { HStack } from '../stack';

export const Alert = memo(
  forwardRef<HTMLDivElement, AlertProps>(
    (
      {
        mode,
        title,
        description,
        closable = false,
        withIcon = true,
        onClose,
        ...restProps
      },
      forwardedRef
    ) => {
      const theme = useTheme();
      const [visible, setVisible] = useState<boolean>(true);
      const [isClosing, setIsClosing] = useState<boolean>(false);
      const {
        wrapperRef,
        measureRef,
        isOverflowing: isMarquee,
      } = useTitleMarquee();

      if (!visible) {
        return null;
      }

      const alertOption = optionsMap[mode];
      const iconColor = theme.semantic.icon[mode];

      const handleClose = () => {
        onClose?.();
        setIsClosing(true);
      };

      return (
        <AlertContainer
          {...restProps}
          ref={forwardedRef}
          $mode={mode}
          $isClosing={isClosing}
          onAnimationEnd={() => setVisible(false)}
        >
          {title && (
            <HStack align="center" spacing="xs" style={{ width: '100%' }}>
              {withIcon && (
                <SVG
                  src={Icons[alertOption.icon]}
                  props={{ color: iconColor }}
                />
              )}

              <TitleWrapper ref={wrapperRef}>
                <MeasureSpan ref={measureRef} aria-hidden>
                  {title}
                </MeasureSpan>
                <TitleTrack
                  $isMarquee={isMarquee}
                  $duration={`${Math.max(8, title.length * 0.2)}s`}
                >
                  <TitleText $isMarquee={isMarquee}>{title}</TitleText>
                  {isMarquee && (
                    <TitleText $isMarquee aria-hidden>
                      {title}
                    </TitleText>
                  )}
                </TitleTrack>
              </TitleWrapper>

              {closable && (
                <SVG
                  src={Icons.Cross}
                  props={{
                    cursor: 'pointer',
                  }}
                  action={handleClose}
                />
              )}
            </HStack>
          )}

          {description && <span>{description}</span>}
        </AlertContainer>
      );
    }
  )
);
